import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authAPI } from '../services/authService';
import {
  APP_PRODUCT_NAME,
  APP_SUBTITLE,
  COMPANY_LOGO,
  POWERED_BY_LOGO,
  POWERED_BY_NAME,
} from '../config/brandingConfig';
import { clearAuthSession } from '../utils/passwordRecovery';
import { getAuthErrorMessage, PASSWORD_HINT } from '../utils/authApiErrors';
import PwaHttpNotice from './PwaHttpNotice';
import PwaInstallPrompt from './PwaInstallPrompt';
import './Login.css';

const Login = () => {
  const navigate = useNavigate();
  const [view, setView] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [loading, setLoading] = useState(false);
  const [showCompanyLogo, setShowCompanyLogo] = useState(true);
  const [showPoweredByLogo, setShowPoweredByLogo] = useState(true);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setInfo('');
    setLoading(true);

    try {
      if (!email || !password) {
        setError('Please enter both email and password.');
        return;
      }
      const response = await authAPI.login({ email, password });
      const token = response?.data?.token || null;
      const user = response?.data?.user || null;

      if (!token || !user) {
        setError('Invalid credentials.');
        return;
      }
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));
      if (user.must_change_password) {
        navigate('/change-password', { replace: true });
      } else {
        navigate('/dashboard', { replace: true });
      }
    } catch (err) {
      console.error('Login failed:', err);
      setError(getAuthErrorMessage(err, 'Login failed. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError('');
    setInfo('');
    if (!email.trim()) {
      setError('Please enter your email address.');
      return;
    }
    setLoading(true);
    try {
      const resp = await authAPI.forgotPassword(email.trim());
      setOtp('');
      setNewPassword('');
      setConfirmPassword('');
      setView('reset-otp');
      setInfo(resp?.data?.message || 'If an account exists for that email, a 6-digit reset code has been sent.');
    } catch (err) {
      setError(getAuthErrorMessage(err, 'Unable to send reset email. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');
    setInfo('');
    if (!email.trim()) {
      setError('Please enter your email address.');
      return;
    }
    const normalizedOtp = String(otp || '').trim();
    if (!/^\d{6}$/.test(normalizedOtp)) {
      setError('Please enter the 6-digit code from your email.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    setLoading(true);
    try {
      const resp = await authAPI.confirmForgotPassword({
        email: email.trim(),
        otp: normalizedOtp,
        newPassword,
      });
      setInfo(resp?.data?.message || 'Password reset successful. You may sign in.');
      clearAuthSession();
      setTimeout(() => {
        navigate('/login', { replace: true });
        setView('login');
        setOtp('');
        setNewPassword('');
        setConfirmPassword('');
      }, 2200);
    } catch (err) {
      setError(getAuthErrorMessage(err, 'Unable to reset password. The code may have expired.'));
    } finally {
      setLoading(false);
    }
  };

  const switchToLogin = () => {
    setView('login');
    setError('');
    setInfo('');
    setOtp('');
    navigate('/login', { replace: true });
  };

  const subtitle = view === 'reset-otp'
    ? 'Enter the code sent to your email'
    : view === 'forgot'
      ? 'Reset your password'
      : APP_SUBTITLE;

  const resetTtlMinutes = 15;

  return (
    <>
      <PwaHttpNotice />
      <PwaInstallPrompt />
      <div className="login-container">
      <div className="login-card">
        <div className="login-layout">
          <aside className="login-showcase" aria-hidden="true">
            <img
              src="/login-features-infographic.png"
              alt=""
              className="login-showcase-image"
            />
          </aside>
          <div className="login-form-panel">
        <div className="login-header">
          <div className="logo">
            <div className="logo-icon">
              {showCompanyLogo && (
                <img
                  src={COMPANY_LOGO}
                  alt="Company Logo"
                  onError={() => setShowCompanyLogo(false)}
                />
              )}
            </div>
            <h2 className="logo-title">{APP_PRODUCT_NAME}</h2>
          </div>
          <p className="subtitle">{subtitle}</p>
        </div>

        {error && (
          <div className="alert alert-error">
            <svg viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            {error}
          </div>
        )}

        {info && (
          <div className="alert alert-info" style={{ background: '#F5F3FF', border: '1px solid #bfdbfe', color: '#6D28D9', padding: '12px 14px', borderRadius: 8, marginBottom: 16, fontSize: 13 }}>
            {info}
          </div>
        )}

        {view === 'reset-otp' && (
          <form className="login-form" onSubmit={handleResetPassword}>
            <p style={{ fontSize: 13, color: '#64748b', marginBottom: 16, lineHeight: 1.5 }}>
              Enter the 6-digit code from your email and choose a new password. The code expires after {resetTtlMinutes} minutes.
            </p>
            <div className="form-group">
              <label htmlFor="reset-email">Email Address <span className="req">*</span></label>
              <input
                id="reset-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                required
                autoComplete="email"
              />
            </div>
            <div className="form-group">
              <label htmlFor="reset-otp">Reset code <span className="req">*</span></label>
              <input
                id="reset-otp"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="6-digit code"
                className="login-otp-input"
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="newPassword">New password <span className="req">*</span></label>
              <input
                id="newPassword"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder={PASSWORD_HINT}
                required
                autoComplete="new-password"
              />
            </div>
            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm password <span className="req">*</span></label>
              <input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                autoComplete="new-password"
              />
            </div>
            <button type="submit" className="btn-login" disabled={loading}>
              {loading ? 'Saving...' : 'Update Password'}
            </button>
            <div style={{ textAlign: 'center', marginTop: 14, display: 'flex', flexDirection: 'column', gap: 8 }}>
              <button type="button" className="login-link-btn" onClick={() => { setView('forgot'); setError(''); setInfo(''); }}>Resend code</button>
              <button type="button" className="login-link-btn" onClick={switchToLogin}>Back to sign in</button>
            </div>
          </form>
        )}

        {view === 'login' && (
          <form className="login-form" onSubmit={handleLogin}>
            <div className="form-group">
              <label htmlFor="email">Email Address <span className="req">*</span></label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="password">Password <span className="req">*</span></label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                required
              />
            </div>
            <div style={{ textAlign: 'right', marginBottom: 12 }}>
              <button type="button" className="login-link-btn" onClick={() => { setView('forgot'); setError(''); setInfo(''); }}>
                Forgot password?
              </button>
            </div>
            <button type="submit" className="btn-login" disabled={loading}>
              {loading ? 'Signing In...' : 'Sign In'}
            </button>
          </form>
        )}

        {view === 'forgot' && (
          <form className="login-form" onSubmit={handleForgotPassword}>
            <p style={{ fontSize: 13, color: '#64748b', marginBottom: 16, lineHeight: 1.5 }}>
              Enter your login email. We will send a 6-digit one-time code to reset your password.
            </p>
            <div className="form-group">
              <label htmlFor="forgot-email">Email Address <span className="req">*</span></label>
              <input
                id="forgot-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                required
              />
            </div>
            <button type="submit" className="btn-login" disabled={loading}>
              {loading ? 'Sending...' : 'Send Reset Code'}
            </button>
            <div style={{ textAlign: 'center', marginTop: 14 }}>
              <button type="button" className="login-link-btn" onClick={switchToLogin}>Back to sign in</button>
            </div>
          </form>
        )}

        <div className="login-footer-note">
          <p>
            {showPoweredByLogo && (
              <img
                src={POWERED_BY_LOGO}
                alt={`${POWERED_BY_NAME} Logo`}
                className="powered-by-logo"
                onError={() => setShowPoweredByLogo(false)}
              />
            )}
            <span>{`Powered by ${POWERED_BY_NAME}`}</span>
          </p>
        </div>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default Login;
